import { DatePart, DateUtil, ModelRow, PermissionsUtil } from "@mcleod/core";
import {
    Button, ButtonVariant, ClickEvent, Component, Image, Label, LocationUtil, Panel, Snackbar, TableRow,
    TableRowDisplayEvent, TableRowMoveEvent, Textbox
} from "@mcleod/components";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutOrderStopTable } from "./autogen/AutogenLayoutOrderStopTable";
import { PlanningUtil } from "./planning/PlanningUtil";
import { AppointmentUtil } from "./AppointmentUtil";

export class OrderStopTable extends AutogenLayoutOrderStopTable {

    /** This is an event handler for the onRowDisplay event of tableStop.  */
    tableStopOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.target as TableRow;
        const modelRow: ModelRow = tableRow.data as ModelRow;

        this.setupLocationName(tableRow, modelRow);
        this.setupBadgeActual(tableRow, modelRow);
        this.setupStopType(tableRow, modelRow)
        AppointmentUtil.setAppointmentLabel(tableRow, "labelApptRequired", "appt_required", "confirmed");
        this.setupArrivalDeparture(tableRow, modelRow);

        tableRow.toolsCell.toolsPanel.visible = false;
        if (!modelRow.get("is_reconsignment")) {
            this.setupRow(tableRow, modelRow);
            this.positionDragHandle(tableRow);
        }
    }

    setupRow(tableRow: TableRow, modelRow: ModelRow) {
        if (modelRow._appending) {
            const labelActionHistory = tableRow.findComponentById("labelActionHistory") as Label;
            const panelToolsManual = tableRow.findComponentById("panelToolsManual") as Panel;

            if (labelActionHistory) {
                labelActionHistory.visible = true;
                labelActionHistory.text = "ADDED STOP";
            }

            if (panelToolsManual) {
                const existingButton = panelToolsManual.findComponentById("buttonDeleteRow");
                if (!existingButton) {
                    const deleteButton = new Button({
                          id: "buttonDeleteRow",
                        imageName: ImageName.circleX,
                          variant: ButtonVariant.round,
                          focusable: false,
                          padding: 0,
                          color: "subtle.darker",
                          rowBreak: false,
                          style: {
                            alignItems: "start",
                            alignContent: "start",
                            verticalAlign: "top"
                          }
                    });
                    deleteButton.addClickListener((event: ClickEvent) => tableRow.deleteRow());
                    panelToolsManual.add(deleteButton);
                }
            }
        }
    }

    setupStopType(tableRow: TableRow, modelRow: ModelRow<any>) {
        const stopType: string = modelRow.get("stop_type");
        const imageStopType = tableRow.findComponentById("imageStopType") as Image ?? null;
        const isPickupType: boolean = LocationUtil.PICKUP_STOP_TYPES.includes(stopType);
        if (imageStopType) {
            imageStopType.name =
                (LocationUtil.VIA_POINT_STOP_TYPES.includes(stopType))
                ? ImageName.viaPoint
                : (isPickupType)
                    ? ImageName.loadPickup
                    : ImageName.loadDelivery;
        }

        const textboxSchedArriveEarly = tableRow.findComponentById("textboxSchedArriveEarly") as Textbox ?? null;
        if (textboxSchedArriveEarly) {
            textboxSchedArriveEarly.caption = isPickupType
                ? "Early Pickup"
                : "Early Delivery";
        }

        const textboxSchedArriveLate = tableRow.findComponentById("textboxSchedArriveLate") as Textbox ?? null;
        if (textboxSchedArriveLate) {
            textboxSchedArriveLate.caption = isPickupType
                ? "Late Pickup"
                : "Late Delivery";
        }
    }

    setupArrivalDeparture(tableRow: TableRow, modelRow: ModelRow<any>) {
        if (modelRow.get("actual_arrival") == null) {
            const textboxActualArrival: Component = tableRow.findComponentById("textboxActualArrival");
            const textboxActualDeparture: Component = tableRow.findComponentById("textboxActualDeparture");
            if (textboxActualArrival != null) {
                textboxActualArrival.visible = false;
            }
            if (textboxActualDeparture != null) {
                textboxActualDeparture.visible = false;
            }
        }
        else {
            const early: Component = tableRow.findComponentById("textboxSchedArriveEarly");
            const apptReq: Component = tableRow.findComponentById("labelApptRequired");
            const late: Component = tableRow.findComponentById("textboxSchedArriveLate");
            if (early != null)
                early.visible = false;
            if (apptReq != null)
                apptReq.visible = false;
            if (late != null)
                late.visible = false;
        }
    }

    setupBadgeActual(tableRow: TableRow, modelRow: ModelRow<any>) {
        if ("A" === modelRow.get("status") && modelRow.get("actual_arrival") != null) {
            tableRow.findComponentById("labelArrivedBadge").visible = true;
        } else if ("D" === modelRow.get("status")) {
            tableRow.findComponentById("labelDepartedBadge").visible = true;
        }
    }

    setupLocationName(tableRow: TableRow, modelRow: ModelRow<any>) {
        const locationName = tableRow.findComponentById("textboxLocationName") as Textbox;
        if (locationName != null) {
            locationName.quickInfoOverrideKeyValue = modelRow.get("location_id");
        }
    }

    positionDragHandle(tableRow: TableRow) {
        // This function is used to move the drag handle inside the border of main content
        const dragHandler = tableRow?.toolsCell?.getDragHandlerImage();
        if (dragHandler != null && !PermissionsUtil.isUserDeniedLayoutItem("lme/dispatch/OrderStopTable", "buttonReorderPlaceholder")) {
            const panelToolsManual = tableRow.findComponentById("panelToolsManual") as Panel;
            panelToolsManual?.add(dragHandler);
        }
    }

    public getSelectedStopData(): ModelRow[] {
        const selectedRows: ModelRow[] = this.tableStop.allRows.filter(row => {
            const buttonStopSelected = row.findComponentById("buttonStopSelected") as Button;
            return buttonStopSelected && buttonStopSelected.selected
        }).map(row => row.data);

        return selectedRows.length > 0 ? selectedRows : null;
    }

    labelApptRequiredOnClick(event: ClickEvent) {
        PlanningUtil.showMultiStopSlideout(event.target as Label, true);
    }

    public clearSelected() {
        this.tableStop.allRows.forEach(row => {
            const buttonStopSelected = row.findComponentById("buttonStopSelected") as Button;
            if (buttonStopSelected) {
                buttonStopSelected.selected = false;
                buttonStopSelected.applyTempState({
                    caption: null,
                    imageName: ImageName.add
                });
            }
        })
    }

    /** This is an event handler for the beforeTableRowMove event of tableStop. */
    tableStopBeforeTableRowMove(event: TableRowMoveEvent) {
        const movedRow = event.getTableRow();
        if (movedRow.data.get("stop_type") == "SP" || movedRow.data.get("stop_type") == "SD") {
            event.stop();
            Snackbar.showWarningSnackbar("Cannot move split point");
            return;
        }
        const movedMovementId = movedRow.data.get("movement_id");
        const { oldIndex, newIndex } = event;
        const prevRow: TableRow = this.tableStop.rows[oldIndex - newIndex > 0 ? newIndex-1 : newIndex];
        const nextRow: TableRow = this.tableStop.rows[oldIndex - newIndex > 0 ? newIndex : newIndex + (newIndex+1 < this.tableStop.rows.length ? 1 : 0)];
        if (prevRow && prevRow.data.get("stop_type") == "SD") {
            event.stop();
            Snackbar.showWarningSnackbar("Cannot move stop between a split point");
        } else if (nextRow && !movedRow.data._appending && movedMovementId != nextRow.data.get("movement_id")) {
            event.stop();
            Snackbar.showWarningSnackbar("Cannot move stop to a different movement");
        } else if (!this.validClearedArrangement(prevRow, nextRow, movedRow.data)) {
            event.stop();
        }
    }

    validClearedArrangement(prevRow: TableRow, nextRow: TableRow, movedRow: ModelRow, appendedMessage: string = "") {
        const nextRowActualArrival = nextRow?.data.getDateTime("actual_arrival", null);
        const prevRowActualArrival = prevRow?.data.getDateTime("actual_arrival", null);
        const prevRowActualDeparture = prevRow?.data.getDateTime("actual_departure", null);
        const nextRowActualDeparture = nextRow?.data.getDateTime("actual_departure", null);
        const currentActualArrival = movedRow.getDateTime("actual_arrival", null);
        const currentActualDeparture = movedRow.getDateTime("actual_departure", null);

        if (nextRow != null) {
            // if next row has departed but moved row has not departed
            if (nextRowActualDeparture && currentActualArrival && !currentActualDeparture) {
                Snackbar.showWarningSnackbar("An arrived stop cannot be before a departed stop." + `\n${appendedMessage}`);
                return false;
            }

            // if next row's actual arrival is before the moved row's actual departure
            if (nextRowActualArrival && currentActualDeparture) {
                const dateDiff = DateUtil.dateDiff(DatePart.SECOND, nextRowActualArrival, currentActualDeparture);
                if (dateDiff < 0) {
                    Snackbar.showWarningSnackbar("The departure of a stop cannot be after the arrival of the next stop." + `\n${appendedMessage}`);
                    return false;
                }
            }

            if (nextRowActualArrival && !currentActualArrival) {
                Snackbar.showWarningSnackbar(`An uncleared stop cannot be placed before a(n) ${nextRowActualDeparture ? "departed" : "arrived"} stop.` + `\n${appendedMessage}`);
                return false;
            }

            if (nextRowActualArrival && !nextRowActualDeparture && currentActualArrival && !currentActualDeparture) {
                Snackbar.showWarningSnackbar("An arrived stop cannot be placed before another arrived stop." + `\n${appendedMessage}`);
                return false;
            }
        }

        if (prevRow != null) {
            // if prev row has not departed but moved row has departed
            if (!prevRowActualDeparture && currentActualDeparture) {
                Snackbar.showWarningSnackbar(`A departed stop cannot be after an ${prevRowActualArrival ? "arrived" : "uncleared"} stop.` + `\n${appendedMessage}`);
                return false;
            }

            // if prev row has departure but moved row actual arrival is before prev departure
            if (prevRowActualDeparture && currentActualArrival) {
                const dateDiff = DateUtil.dateDiff(DatePart.SECOND, currentActualArrival, prevRowActualDeparture);
                if (dateDiff < 0) {
                    Snackbar.showWarningSnackbar("The arrival of a stop cannot be before the departure of the previous stop." + `\n${appendedMessage}`);
                    return false;
                }
            }

            // if moved row has arrival or departure but prev row doesn't have departure
            if (!prevRowActualArrival && currentActualArrival) {
                Snackbar.showWarningSnackbar(`A(n) ${currentActualDeparture ? "departed" : "arrived"} stop cannot be placed after an uncleared stop.` + `\n${appendedMessage}`);
                return false;
            }
        }
        return true;
    }
}
